import * as React from 'react';

import {
  EventSponsor,
  EventSponsorTiersByEventTypeResponse,
  getEventSponsorTiersByEventType,
} from '@/api/sponsor/eventSponsors';
import useApi from '@/hooks/useApi';
import { useCurrent } from '@/hooks/useCurrent';
import { EventSponsorEventType } from '@/imports/ui/admin/pages/Sponsor/eventSponsors/components/constants';

export const FEATURED_SPONSOR_BASE_TEMPLATE = 'Featured Sponsor';
export const ADDITIONAL_SPONSORS_BASE_TEMPLATE = 'Additional Sponsors';

export const useEventSponsorTiersByEventType = (
  eventType: string = EventSponsorEventType.SimpleEvent
) => {
  const current = useCurrent();
  const tenantId = current?.tenant?._id ?? '';

  const {
    data: tiersResponse,
    loading,
    execute,
  } = useApi<EventSponsorTiersByEventTypeResponse>(
    getEventSponsorTiersByEventType
  );

  React.useEffect(() => {
    if (!tenantId || !eventType) return;

    void execute({ tenantId, eventType }).catch(() => {
      // useApi throws on failure; keep the editors mounted with empty tiers
    });
  }, [tenantId, eventType, execute]);

  const tiers = React.useMemo(() => {
    const items = tiersResponse?.data ?? [];
    return [...items].sort((a, b) => a.order - b.order);
  }, [tiersResponse]);

  const featuredSponsorTiers = React.useMemo(
    () =>
      tiers.filter(
        (tier) => tier.baseTemplate === FEATURED_SPONSOR_BASE_TEMPLATE
      ),
    [tiers]
  );

  const additionalSponsorTiers = React.useMemo(
    () =>
      tiers.filter(
        (tier) => tier.baseTemplate === ADDITIONAL_SPONSORS_BASE_TEMPLATE
      ),
    [tiers]
  );

  const conferenceTiers = React.useMemo(
    () => (eventType === EventSponsorEventType.Conference ? tiers : []),
    [eventType, tiers]
  );

  return {
    loading,
    tiers,
    featuredSponsorTiers,
    additionalSponsorTiers,
    conferenceTiers,
  };
};
